import { useState, useEffect } from 'react';

interface CountdownProps {
  title?: string;
  targetDate?: string;
  expiredMessage?: string;
  showDays?: boolean;
  showHours?: boolean;
  showMinutes?: boolean;
  showSeconds?: boolean;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  expired: boolean;
}

function calcTimeLeft(targetDate: string): TimeLeft {
  const diff = new Date(targetDate).getTime() - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const minutes = Math.floor((diff % 3_600_000) / 60_000);
  const seconds = Math.floor((diff % 60_000) / 1_000);
  return { days, hours, minutes, seconds, expired: false };
}

function Pad({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <span className="text-3xl sm:text-4xl font-bold tabular-nums text-zinc-900">
        {String(value).padStart(2, '0')}
      </span>
      <span className="text-xs text-zinc-500 uppercase tracking-wide">{label}</span>
    </div>
  );
}

export function CountdownBlock({ props }: { props: CountdownProps }) {
  const showDays    = props.showDays    !== false;
  const showHours   = props.showHours   !== false;
  const showMinutes = props.showMinutes !== false;
  const showSeconds = props.showSeconds !== false;

  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(
    props.targetDate ? calcTimeLeft(props.targetDate) : null,
  );

  useEffect(() => {
    if (!props.targetDate) return;
    setTimeLeft(calcTimeLeft(props.targetDate));
    const interval = setInterval(() => {
      const t = calcTimeLeft(props.targetDate!);
      setTimeLeft(t);
      if (t.expired) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [props.targetDate]);

  if (!props.targetDate) return null;

  return (
    <div className="py-8 px-4 text-center">
      {props.title && (
        <h2 className="text-xl font-bold text-zinc-900 mb-6">{props.title}</h2>
      )}
      {timeLeft?.expired ? (
        <p className="text-zinc-500 text-base">{props.expiredMessage || 'This event has ended.'}</p>
      ) : timeLeft ? (
        <div className="flex items-start justify-center gap-4 sm:gap-8">
          {showDays    && <Pad value={timeLeft.days}    label="Days"    />}
          {showHours   && <Pad value={timeLeft.hours}   label="Hours"   />}
          {showMinutes && <Pad value={timeLeft.minutes} label="Minutes" />}
          {showSeconds && <Pad value={timeLeft.seconds} label="Seconds" />}
        </div>
      ) : null}
    </div>
  );
}
